// ============================================
// ENUMS - Alignés avec le backend Prisma
// ============================================

export enum IssueType {
  TECHNICAL = 'TECHNICAL',
  DELIVERY = 'DELIVERY',
  BILLING = 'BILLING',
  OTHER = 'OTHER',
}

export enum TicketStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_CUSTOMER = 'WAITING_CUSTOMER',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
  ESCALATED = 'ESCALATED',
  REOPENED = 'REOPENED',
}

export enum TicketPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export enum UserRole {
  CUSTOMER = 'CUSTOMER',
  AGENT = 'AGENT',
  SUPERVISOR = 'SUPERVISOR',
  ADMIN = 'ADMIN',
}

export enum NotificationType {
  MESSAGE = 'MESSAGE',
  MENTION = 'MENTION',
  TICKET_UPDATE = 'TICKET_UPDATE',
  SLA_WARNING = 'SLA_WARNING',
  SLA_BREACH = 'SLA_BREACH',
}

// ============================================
// INTERFACES - Utilisateur
// ============================================

export interface User {
  id: string;
  email: string;
  phone?: string;
  displayName: string;
  role: UserRole | string;
  avatarUrl?: string;
  isActive?: boolean;
  lastSeenAt?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt?: string;
  // Stats from backend
  _count?: {
    assignedTickets?: number;
    tickets?: number;
    messages?: number;
  };
  activeTicketsCount?: number; // For available agents endpoint
  /** Compte client : code client SAGE et compte klygroupe.com relié. */
  customerCode?: string | null;
  prestashopCustomerId?: number | null;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
  /** Vrai si le compte doit choisir un nouveau mot de passe avant de continuer. */
  mustChangePassword?: boolean;
}

// ============================================
// INTERFACES - Commandes
// ============================================

export interface OrderItem {
  ref: string;
  name: string;
  quantity: number;
  imageUrl?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  plNumber?: string;
  blNumber?: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  items: OrderItem[];
  purchaseDate: string;
  status: 'DELIVERED' | 'IN_TRANSIT' | 'PROCESSING';
}

// ============================================
// INTERFACES - Tickets
// ============================================

export interface Attachment {
  id: string;
  url: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface TicketHistory {
  id: string;
  action: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  actor?: User;
  /** Détails libres posés par le serveur (ex. `raison` d'une clôture automatique). */
  metadata?: Record<string, unknown> | null;
  createdAt: Date;
}

export interface Ticket {
  id: string;
  ticketNumber?: number;
  ticketRef?: string; // Format: 26-0001 (année-numéro)
  title: string;
  description?: string;
  status: TicketStatus;
  priority: TicketPriority;
  issueType: IssueType;
  slaDeadline?: Date | string;
  slaBreached: boolean;
  tags: string[];
  customerId?: string;
  customer?: User;
  assignedToId?: string;
  assignedTo?: User;
  orderId?: string;
  order?: Order;
  attachments?: Attachment[];
  messages?: TicketMessage[];
  satisfactionScore?: number;
  history?: TicketHistory[];
  createdAt: Date | string;
  updatedAt: Date | string;
  companyName?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  // Informations équipement
  serialNumber?: string;
  equipmentModel?: string;
  equipmentBrand?: string;
  errorCode?: string;
  /** Né d'un e-mail jugé indésirable par Microsoft : en attente de la
   *  décision du personnel (« Pas un spam » ou « Spam »). */
  spamPossible?: boolean;
  /** Clos comme « Pas une demande SAV » (sans notification au client). */
  horsSav?: boolean;
  /** Expéditeur sans compte relié à une société cliente par le domaine de
   *  son adresse (technicien@clim-dupont.fr → client CLIM DUPONT). null ou
   *  absent : pas de rattachement par le domaine. Forme brute du serveur :
   *  la lire avec `lireRattachementDomaine` (features/tickets/domaine.ts). */
  rattachementDomaine?: RattachementDomaineBrut | null;
  /** L'assistant automatique ne parle plus au client sur ce dossier (définitif). */
  aiMuted?: boolean;
  /** Pourquoi : « un conseiller a répondu », « le client a demandé une personne »… */
  aiMutedReason?: string | null;
  _count?: {
    messages?: number;
  };
}

/** Rattachement par le domaine tel que le serveur le renvoie. Le serveur
 *  stocke le statut PROPOSE (à confirmer), CONFIRME ou REFUSE ; A_CONFIRMER
 *  est accepté comme synonyme de PROPOSE. */
export interface RattachementDomaineBrut {
  domaine: string;
  societe?: string | null;
  compteId?: string | null;
  compteNom?: string | null;
  compteEmail?: string | null;
  customerCode?: string | null;
  statut: 'A_CONFIRMER' | 'PROPOSE' | 'CONFIRME' | 'REFUSE' | string;
  [autre: string]: unknown;
}

/** Rattachement d'un dossier à une société cliente par le domaine de
 *  l'adresse de l'expéditeur (posé par le tri des e-mails), ramené à la
 *  forme de l'écran. */
export interface RattachementDomaine {
  /** Domaine d'entreprise, sans « @ » (ex. clim-dupont.fr). */
  domaine: string;
  /** Nom de la société cliente (ex. CLIM DUPONT), s'il est connu. */
  societe: string | null;
  /** Compte client du SAV proposé pour cette société ; null s'il n'y en a pas. */
  compteId: string | null;
  /** Nom de ce compte, s'il est connu. */
  compteNom?: string | null;
  /** Adresse de ce compte, montrée au superviseur avant la confirmation. */
  compteEmail?: string | null;
  /** Code client de la société sur la boutique : le serveur s'en sert pour
   *  retrouver un compte créé après la proposition. */
  customerCode?: string | null;
  statut: 'A_CONFIRMER' | 'CONFIRME';
  /** ADRESSE : l'expéditeur écrit depuis l'adresse exacte d'un compte de la
   *  boutique (22/10/2026) ; la confirmation crée son compte SAV et relie le
   *  dossier en un clic. DOMAINE (par défaut) : rattachement par le domaine. */
  origine?: 'DOMAINE' | 'ADRESSE';
  /** ADRESSE : prénom et nom du titulaire du compte de la boutique. */
  clientNom?: string | null;
}

export interface UpdateTicketPayload {
  status?: TicketStatus;
  priority?: TicketPriority;
  issueType?: IssueType;
  assignedToId?: string | null;
  tags?: string[];
  // Équipement (null efface la valeur)
  serialNumber?: string | null;
  equipmentBrand?: string | null;
  equipmentModel?: string | null;
  errorCode?: string | null;
  /** Archivage « Sans prévenir le client » : pas d'avis de changement de statut pour cette mise à jour. */
  sansNotification?: boolean;
}

/** Corps de POST /admin/tickets (création par un membre du personnel). */
export interface CreateTicketPayload {
  title: string;
  description?: string;
  issueType: IssueType;
  priority?: TicketPriority;
  customerId?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  companyName?: string;
  serialNumber?: string;
  equipmentBrand?: string;
  equipmentModel?: string;
  errorCode?: string;
  tags?: string[];
}

// ============================================
// INTERFACES - Messages
// ============================================

export interface TicketMessage {
  id: string;
  ticketId: string;
  authorId: string;
  author?: User;
  content: string;
  isInternal?: boolean;
  readBy?: string[];
  attachments?: Attachment[];
  createdAt: Date;
  /** Message de l'assistant automatique (compte technique), pas d'un conseiller. */
  estAssistant?: boolean;
  /** Réponse par e-mail dont l'historique cité a été retiré : l'e-mail
   *  complet est consultable (« Voir l'e-mail complet »). */
  emailComplet?: boolean;
}

/** E-mail d'origine complet d'un message (GET …/email-complet). */
export interface EmailComplet {
  de: string;
  objet: string | null;
  recuLe: string;
  texte: string;
}

// ============================================
// INTERFACES - Notifications
// ============================================

export interface Notification {
  id: string;
  userId?: string;
  type: NotificationType;
  ticketId?: string;
  ticketNumber?: number; // Numéro de ticket lisible
  ticketTitle?: string; // Titre du ticket
  messageId?: string;
  payload?: Record<string, unknown>;
  // For real-time notifications
  title?: string;
  body?: string;
  isRead: boolean;
  createdAt: Date | string;
}

// ============================================
// INTERFACES - Pagination & Filtres
// ============================================

export interface TicketFilters {
  page?: number;
  limit?: number;
  status?: TicketStatus;
  excludeStatus?: TicketStatus[];
  issueType?: IssueType;
  priority?: TicketPriority;
  assignedToId?: string;
  search?: string;
  /** Date de création minimale (AAAA-MM-JJ). */
  dateFrom?: string;
  /** Date de création maximale (AAAA-MM-JJ). */
  dateTo?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'priority' | 'status' | 'slaDeadline' | 'ticketNumber' | 'title';
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============================================
// INTERFACES - Statistiques
// ============================================

export interface TicketStats {
  total: number;
  byStatus: Record<TicketStatus, number>;
  byPriority: Record<TicketPriority, number>;
  byIssueType: Record<IssueType, number>;
  slaBreached: number;
  avgResolutionTime?: number;
}

// ============================================
// INTERFACES - Réponses types
// ============================================

/** Réponse type en base (GET /admin/canned-responses → `{ responses }`). */
export interface CannedResponse {
  id: string;
  title: string;
  content: string;
  tags: string[];
}

// ============================================
// ENUMS - Automation
// ============================================

export enum AutomationTrigger {
  TICKET_CREATED = 'TICKET_CREATED',
  TICKET_UPDATED = 'TICKET_UPDATED',
  TICKET_STATUS_CHANGED = 'TICKET_STATUS_CHANGED',
  TICKET_RESOLVED = 'TICKET_RESOLVED',
  TICKET_CLOSED = 'TICKET_CLOSED',
  TICKET_IDLE_4H = 'TICKET_IDLE_4H',
  TICKET_WAITING_3DAYS = 'TICKET_WAITING_3DAYS',
  TICKET_RESOLVED_7DAYS = 'TICKET_RESOLVED_7DAYS',
  SLA_WARNING = 'SLA_WARNING',
  SLA_BREACH = 'SLA_BREACH',
}

// ============================================
// INTERFACES - Automation
// ============================================

export interface AutomationCondition {
  field: string;
  operator: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'in';
  value: string | number | boolean | string[];
}

export interface AutomationAction {
  type: string;
  params?: Record<string, unknown>;
}

export interface AutomationRule {
  id: string;
  name: string;
  description?: string;
  trigger: AutomationTrigger;
  conditions: AutomationCondition[];
  actions: AutomationAction[];
  isActive: boolean;
  priority: number;
  createdById?: string;
  createdBy?: {
    id: string;
    displayName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  _count?: {
    executions: number;
  };
}

export interface AutomationStats {
  totalRules: number;
  activeRules: number;
  todayExecutions: number;
  weekExecutions: number;
  autoAssignCount: number;
  notificationCount: number;
}

// ============================================
// INTERFACES - Base de connaissances (Marques)
// ============================================

export interface Brand {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string;
  folderUrl?: string;    // Lien vers le dossier externe (SharePoint/Drive)
  websiteUrl?: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// PARAMÈTRES APPLICATIFS
// ============================================

export type TypeParametre = 'BOOLEEN' | 'TEXTE' | 'NOMBRE' | 'SECRET' | 'JSON';

export interface Parametre {
  cle: string;
  categorie: string;
  libelle: string;
  description: string;
  type: TypeParametre;
  options?: string[];
  /** Un paramètre sensible n'est jamais renvoyé en clair par l'API. */
  sensible?: boolean;
  /** Toujours null pour un paramètre sensible. */
  valeur: string | null;
  /** Vrai si une valeur a été saisie — utile pour les secrets. */
  renseigne: boolean;
  parDefaut: boolean;
}

// ============================================
// CONNECTEURS DE BOÎTE MAIL
// ============================================

export type FournisseurMail = 'MICROSOFT' | 'GOOGLE';
export type EtatConnecteur = 'INACTIF' | 'ACTIF' | 'ERREUR';

export interface MailConnecteur {
  id: string;
  label: string;
  provider: FournisseurMail;
  mailbox: string;
  tenantId: string | null;
  clientId: string;
  secretConfigure: boolean;
  status: EtatConnecteur;
  lastError: string | null;
  lastSyncAt: string | null;
  subscriptionExpiresAt: string | null;
}

export interface EntreeConnecteur {
  label: string;
  provider: FournisseurMail;
  mailbox: string;
  tenantId?: string | null;
  clientId: string;
  /** Laisser vide à la modification signifie « inchangé ». */
  clientSecret?: string;
}

export interface ResultatTestConnecteur {
  ok: boolean;
  mailbox?: string;
  messagesVus?: number;
  motif?: string;
}
